import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import Tooltip from "aldoo-ra/Tooltip"
import { PhotoIcon } from "@heroicons/react/24/outline"
import { url, getRelativePath } from "aldoo-ra/CMS/url.js"

const ImagePropertyEditor = ({
  property,
  value,
  onChange,
  propertyConfig,
  className = ""
}) => {
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  if (!property || !propertyConfig) return null

  // Extract URL from background-image CSS value
  const extractImageUrl = (backgroundImageValue) => {
    if (!backgroundImageValue) return null
    const match = backgroundImageValue.match(/url\(['"]?(.*?)['"]?\)/)
    return match ? match[1] : null
  }

  const handleInputChange = (e) => {
    onChange?.(property, e.target.value)
  }

  const handleMediaSelect = (file) => {
    const baseUrl = file.url || file.path || ""
    let fullUrl, relativePath

    if (!baseUrl.startsWith('http')) {
      fullUrl = url(baseUrl)
      relativePath = baseUrl
    } else {
      fullUrl = baseUrl
      relativePath = getRelativePath(baseUrl)
    }

    // For background-image property, format as CSS url()
    const cssValue = property === 'background-image' ? `url('${fullUrl}')` : fullUrl

    onChange?.(property, cssValue)

    // If this is a background image, also set the CDN path
    if (property === 'background-image' && onChange) {
      onChange('background-image-cdn', relativePath)
    }

    setIsMediaLibraryOpen(false)
  }

  const imageUrl = property === 'background-image' ? extractImageUrl(value) : value

  return (
    <>
      <div className={`space-y-2 ${className}`}>
        <Typography
          variant="admin_body_small"
          className="font-medium capitalize text-admin_text_secondary text-sm"
        >
          {propertyConfig.label || property}
        </Typography>
        <div className="flex gap-2 items-center">
          <Input
            variant="admin"
            value={value || ""}
            onChange={handleInputChange}
            placeholder={propertyConfig.placeholder || ""}
            debounce={300}
            className="flex-1"
          />

          {/* Thumbnail preview */}
          {imageUrl && (
            <Tooltip
              content={
                <div className="p-1">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-w-xs max-h-48 object-contain rounded"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </div>
              }
              placement="top"
            >
              <div className="w-8 h-8 border border-admin_bordered rounded overflow-hidden bg-admin_neutral_1 flex-shrink-0">
                <img
                  src={imageUrl}
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <PhotoIcon className="w-full h-full p-1 text-admin_text_secondary hidden" />
              </div>
            </Tooltip>
          )}

          <Button
            variant="admin_secondary"
            onClick={() => setIsMediaLibraryOpen(true)}
            className="px-3 flex-shrink-0"
          >
            Browse
          </Button>
        </div>
      </div>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onFileSelect={handleMediaSelect}
        title={`Select ${propertyConfig.label || property}`}
        defaultFileType="image"
      />
    </>
  )
}

export default ImagePropertyEditor